/**
 * Copyright (c) 2013-present, Facebook, Inc.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree. An additional grant
 * of patent rights can be found in the PATENTS file in the same directory.
 *
 * @providesModule RelayModernEnvironment
 * 
 * @format
 */

'use strict';

var _classCallCheck3 = _interopRequireDefault(require('babel-runtime/helpers/classCallCheck'));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { 'default': obj }; }

var RelayModernEnvironment = function () {
  function RelayModernEnvironment(config) {
    var _this = this;

    (0, _classCallCheck3['default'])(this, RelayModernEnvironment);

    this.configName = config.configName;
    var handlerProvider = config.handlerProvider ? config.handlerProvider : require('./RelayDefaultHandlerProvider');
    this._network = config.network;
    this._publishQueue = new (require('./RelayPublishQueue'))(config.store, handlerProvider);
    this._store = config.store;
    this.unstable_internal = require('./RelayCore');

    this.__setNet = function (newNet) {
      return _this._network = newNet;
    };

    if (process.env.NODE_ENV !== 'production') {
      var g = typeof global !== 'undefined' ? global : window;

      // Attach the debugger symbol to the global symbol so it can be accessed by
      // devtools extension.
      if (!g.__RELAY_DEBUGGER__) {
        var _require = require('./RelayDebugger'),
            RelayDebugger = _require.RelayDebugger;

        g.__RELAY_DEBUGGER__ = new RelayDebugger();
      }

      var envId = g.__RELAY_DEBUGGER__.registerEnvironment(this);
      this._debugger = g.__RELAY_DEBUGGER__.getEnvironmentDebugger(envId);
    } else {
      this._debugger = null;
    }
  }

  RelayModernEnvironment.prototype.getStore = function getStore() {
    return this._store;
  };

  RelayModernEnvironment.prototype.getDebugger = function getDebugger() {
    return this._debugger;
  };

  RelayModernEnvironment.prototype.applyUpdate = function applyUpdate(optimisticUpdate) {
    var _this2 = this;

    var dispose = function dispose() {
      _this2._publishQueue.revertUpdate(optimisticUpdate);
      _this2._publishQueue.run();
    };
    this._publishQueue.applyUpdate(optimisticUpdate);
    this._publishQueue.run();
    return { dispose: dispose };
  };

  RelayModernEnvironment.prototype.revertUpdate = function revertUpdate(update) {
    this._publishQueue.revertUpdate(update);
    this._publishQueue.run();
  };

  RelayModernEnvironment.prototype.replaceUpdate = function replaceUpdate(update, newUpdate) {
    this._publishQueue.revertUpdate(update);
    this._publishQueue.applyUpdate(newUpdate);
    this._publishQueue.run();
  };

  RelayModernEnvironment.prototype.applyMutation = function applyMutation(_ref) {
    var operation = _ref.operation,
        optimisticResponse = _ref.optimisticResponse,
        optimisticUpdater = _ref.optimisticUpdater;

    return this.applyUpdate({
      operation: operation,
      selectorStoreUpdater: optimisticUpdater,
      response: optimisticResponse || null
    });
  };

  RelayModernEnvironment.prototype.check = function check(readSelector) {
    return this._store.check(readSelector);
  };

  RelayModernEnvironment.prototype.commitPayload = function commitPayload(operationSelector, payload) {
    // Do not handle stripped nulls when commiting a payload
    var relayPayload = require('./normalizeRelayPayload')(operationSelector.root, payload);
    this._publishQueue.commitPayload(operationSelector, relayPayload);
    this._publishQueue.run();
  };

  RelayModernEnvironment.prototype.commitUpdate = function commitUpdate(updater) {
    this._publishQueue.commitUpdate(updater);
    this._publishQueue.run();
  };

  RelayModernEnvironment.prototype.lookup = function lookup(readSelector) {
    return this._store.lookup(readSelector);
  };

  RelayModernEnvironment.prototype.subscribe = function subscribe(snapshot, callback) {
    return this._store.subscribe(snapshot, callback);
  };

  RelayModernEnvironment.prototype.retain = function retain(selector) {
    return this._store.retain(selector);
  };

  RelayModernEnvironment.prototype.sendQuery = function sendQuery(_ref2) {
    var _this3 = this;

    var cacheConfig = _ref2.cacheConfig,
        onCompleted = _ref2.onCompleted,
        onError = _ref2.onError,
        onNext = _ref2.onNext,
        operation = _ref2.operation;

    var isDisposed = false;
    var dispose = function dispose() {
      isDisposed = true;
    };
    var onRequestSuccess = function onRequestSuccess(payload) {
      if (isDisposed) {
        return;
      }
      _this3._publishQueue.commitPayload(operation, payload);
      _this3._publishQueue.run();
      onNext && onNext(payload);
      onCompleted && onCompleted();
    };
    var onRequestError = function onRequestError(error) {
      if (isDisposed) {
        return;
      }
      onError && onError(error);
    };
    var networkRequest = this._network.request(operation.node, operation.variables, cacheConfig);
    if (require('./isPromise')(networkRequest)) {
      networkRequest.then(onRequestSuccess)['catch'](onRequestError);
    } else if (networkRequest instanceof Error) {
      onRequestError(networkRequest);
    } else {
      onRequestSuccess(networkRequest);
    }
    return { dispose: dispose };
  };

  RelayModernEnvironment.prototype.streamQuery = function streamQuery(_ref3) {
    var _this4 = this;

    var cacheConfig = _ref3.cacheConfig,
        onCompleted = _ref3.onCompleted,
        onError = _ref3.onError,
        _onNext = _ref3.onNext,
        operation = _ref3.operation;

    return this._network.requestStream(operation.node, operation.variables, cacheConfig, {
      onCompleted: onCompleted,
      onError: onError,
      onNext: function onNext(payload) {
        _this4._publishQueue.commitPayload(operation, payload);
        _this4._publishQueue.run();
        _onNext && _onNext(payload);
      }
    });
  };

  RelayModernEnvironment.prototype.sendMutation = function sendMutation(_ref4) {
    var _this5 = this;

    var onCompleted = _ref4.onCompleted,
        onError = _ref4.onError,
        operation = _ref4.operation,
        optimisticResponse = _ref4.optimisticResponse,
        optimisticUpdater = _ref4.optimisticUpdater,
        updater = _ref4.updater,
        uploadables = _ref4.uploadables;

    var mutationUid = nextMutationUid();
    var hasOptimisticUpdate = !!optimisticResponse || optimisticUpdater;
    var optimisticUpdate = {
      operation: operation,
      selectorStoreUpdater: optimisticUpdater,
      response: optimisticResponse || null
    };
    if (hasOptimisticUpdate) {
      this._recordDebuggerEvent({
        eventName: 'optimistic_update',
        mutationUid: mutationUid,
        operation: operation,
        fn: function fn() {
          _this5._publishQueue.applyUpdate(optimisticUpdate);
          _this5._publishQueue.run();
        }
      });
    }
    var isDisposed = false;
    var dispose = function dispose() {
      if (hasOptimisticUpdate) {
        _this5._recordDebuggerEvent({
          eventName: 'optimistic_revert',
          mutationUid: mutationUid,
          operation: operation,
          fn: function fn() {
            _this5._publishQueue.revertUpdate(optimisticUpdate);
            _this5._publishQueue.run();
            hasOptimisticUpdate = false;
          }
        });
      }
      isDisposed = true;
    };
    var onRequestSuccess = function onRequestSuccess(payload) {
      if (isDisposed) {
        return;
      }

      _this5._recordDebuggerEvent({
        eventName: 'request_commit',
        mutationUid: mutationUid,
        operation: operation,
        payload: payload,
        fn: function fn() {
          if (hasOptimisticUpdate) {
            _this5._publishQueue.revertUpdate(optimisticUpdate);
          }
          _this5._publishQueue.commitPayload(operation, payload, updater);
          _this5._publishQueue.run();
        }
      });

      onCompleted && onCompleted(payload.errors);
    };

    var onRequestError = function onRequestError(error) {
      if (isDisposed) {
        return;
      }

      _this5._recordDebuggerEvent({
        eventName: 'request_error',
        mutationUid: mutationUid,
        operation: operation,
        payload: error,
        fn: function fn() {
          if (hasOptimisticUpdate) {
            _this5._publishQueue.revertUpdate(optimisticUpdate);
          }
          _this5._publishQueue.run();
        }
      });
      onError && onError(error);
    };

    var networkRequest = this._network.request(operation.node, operation.variables, { force: true }, uploadables);

    if (require('./isPromise')(networkRequest)) {
      networkRequest.then(onRequestSuccess)['catch'](onRequestError);
    } else {
      require('fbjs/lib/warning')(false, 'RelayModernEnvironment: mutation request cannot be synchronous.');
    }
    return { dispose: dispose };
  };

  RelayModernEnvironment.prototype.sendSubscription = function sendSubscription(_ref5) {
    var _this6 = this;

    var onCompleted = _ref5.onCompleted,
        _onNext2 = _ref5.onNext,
        onError = _ref5.onError,
        operation = _ref5.operation,
        updater = _ref5.updater;

    return this._network.requestStream(operation.node, operation.variables, { force: true }, {
      onCompleted: onCompleted,
      onError: onError,
      onNext: function onNext(payload) {
        _this6._publishQueue.commitPayload(operation, payload, updater);
        _this6._publishQueue.run();
        _onNext2 && _onNext2(payload);
      }
    });
  };

  RelayModernEnvironment.prototype._recordDebuggerEvent = function _recordDebuggerEvent(_ref6) {
    var eventName = _ref6.eventName,
        mutationUid = _ref6.mutationUid,
        operation = _ref6.operation,
        payload = _ref6.payload,
        fn = _ref6.fn;

    if (this._debugger) {
      this._debugger.recordMutationEvent({
        eventName: eventName,
        payload: payload,
        fn: fn,
        mutation: operation,
        seriesId: mutationUid
      });
    } else {
      fn();
    }
  };

  RelayModernEnvironment.prototype.checkSelectorAndUpdateStore = function checkSelectorAndUpdateStore(selector, handlers) {
    var target = new (require('./RelayInMemoryRecordSource'))();
    var result = require('./RelayDataLoader').check(this._store.getSource(), target, selector, handlers);
    if (target.size() > 0) {
      this._publishQueue.commitSource(target);
      this._publishQueue.run();
    }
    return result;
  };

  return RelayModernEnvironment;
}();

var mutationUidCounter = 0;
var mutationUidPrefix = Math.random().toString();
function nextMutationUid() {
  return mutationUidPrefix + mutationUidCounter++;
}

// Add a sigil for detection by `isRelayModernEnvironment()` to avoid a
// realm-specific instanceof check, and to aid in module tree-shaking to
// avoid requiring all of RelayRuntime just to detect its environment.
RelayModernEnvironment.prototype['@@RelayModernEnvironment'] = true;

module.exports = RelayModernEnvironment;


//////////////////
// WEBPACK FOOTER
// ./~/react-relay/~/relay-runtime/lib/RelayModernEnvironment.js
// module id = 3399
// module chunks = 3